import { REGIONS } from './constants';
import { TAB_TYPES } from '../../constants/tab';
import translations from '../../translations';
import { formatTenantName } from '../../utils/tenant';

export const MASTHEAD_NAV_IDS = {
    switchTenant: 'switch-tenant',
    settings: 'settings',
    regions: 'regions',
} as const;

export const MASTHEAD_TAB_NAV_IDS: string[] = [
    TAB_TYPES.userSettings,
    TAB_TYPES.organization,
    TAB_TYPES.tenant,
    TAB_TYPES.environments,
];

export interface MastheadSubMenu {
    id: string;
    title: string;
    description: string;
    link: string;
    selected: boolean;
}

export interface MastheadNavItem {
    id: string;
    title: string;
    link: string;
    selected: boolean;
    submenus?: MastheadSubMenu[];
}

export interface MastheadSubNavParams {
    currentTenantName?: string | undefined;
    isAdministrator?: boolean;
    showEnvironmentMenuItem?: boolean;
    accountMode?: boolean;
    hasTenant?: boolean;
}

const platformDomain = 'https://flow.boomi.com';
const legacyPlatformDomain = 'https://flow.manywho.com';

const buildRegionSubmenus = (): MastheadSubMenu[] => {
    const host = `${window.location.protocol}//${window.location.hostname}`;

    return Object.entries(REGIONS).map(([key, region]) => {
        const isGlobalRegion = region.name === REGIONS.us.name;
        const isSelected =
            region.url === host ||
            (isGlobalRegion && host === platformDomain) ||
            (isGlobalRegion && host === legacyPlatformDomain);

        return {
            id: key,
            title: region.name,
            description: region.summary,
            link: region.url,
            selected: isSelected,
        };
    });
};

const buildSettingsSubmenus = (
    showEnvironmentMenuItem: boolean,
    accountMode: boolean,
): MastheadSubMenu[] => {
    const submenus: MastheadSubMenu[] = accountMode
        ? [
              {
                  id: TAB_TYPES.tenant,
                  title: `${translations.HEADER_account_link_text}`,
                  description: translations.HEADER_account_summary_text,
                  link: '#',
                  selected: false,
              },
          ]
        : [
              {
                  id: TAB_TYPES.organization,
                  title: `${translations.HEADER_org_link_text}`,
                  description: translations.HEADER_org_summary_text,
                  link: '#',
                  selected: false,
              },
              {
                  id: TAB_TYPES.tenant,
                  title: `${translations.HEADER_tenant_link_text}`,
                  description: translations.HEADER_tenant_summary_text,
                  link: '#',
                  selected: false,
              },
          ];

    if (showEnvironmentMenuItem) {
        submenus.push({
            id: TAB_TYPES.environments,
            title: `${translations.HEADER_environments_link_text}`,
            description: translations.HEADER_environments_summary_text,
            link: '#',
            selected: false,
        });
    }

    return submenus;
};

export const buildMastheadSubNav = ({
    currentTenantName,
    isAdministrator = false,
    showEnvironmentMenuItem = false,
    accountMode = false,
    hasTenant = true,
}: MastheadSubNavParams): MastheadNavItem[] => {
    const items: MastheadNavItem[] = [];

    // Switching tenant and user settings only make sense once a tenant has been selected
    if (hasTenant) {
        if (!accountMode) {
            items.push({
                id: MASTHEAD_NAV_IDS.switchTenant,
                title: currentTenantName
                    ? formatTenantName(currentTenantName)
                    : translations.HEADER_change_tenant,
                link: '#',
                selected: false,
                submenus: [],
            });
        }

        items.push({
            id: TAB_TYPES.userSettings,
            title: translations.HEADER_user_settings,
            link: '#',
            selected: false,
            submenus: [],
        });
    }

    if (isAdministrator) {
        items.push({
            id: MASTHEAD_NAV_IDS.settings,
            title: translations.HEADER_settings,
            link: '#',
            selected: false,
            submenus: buildSettingsSubmenus(showEnvironmentMenuItem, accountMode),
        });
    }

    items.push({
        id: MASTHEAD_NAV_IDS.regions,
        title: translations.REGION_SWITCHER_button_text,
        link: '#',
        selected: false,
        submenus: buildRegionSubmenus(),
    });

    return items;
};
